import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import UnstyledImage from "./image";
import { Link } from "gatsby";
import { faArrowRightLong } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { _ } from "../utils/i18n";

const SearchResultsGrid = styled.div`
  display: flex;
  flex: initial;
  align-items: stretch;
  flex-direction: column;
  box-sizing: border-box;
  background-color: var(--color-white);
  padding: 0.875rem;
  justify-content: flex-start;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  transition: 0.3s all ease-in-out;
  @media (hover: hover) {
    &:hover {
      box-shadow: var(--box-shadow);
      border: 1px solid var(--color-accent2);
      & h2 {
        text-decoration: underline;
      }
    }
  }
`;
const Image = styled(UnstyledImage)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.4s ease-in-out !important;
  @media (hover: hover) {
    &:hover {
      transform: scale(1.04);
      cursor: pointer;
    }
  }
`;
const TextDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;
const Date = styled.p`
  font-size: 0.8rem;
  margin-top: 1rem;
`;
const Title = styled.h2`
  font-weight: 300;
  font-size: 18px;
  margin-bottom: 15px !important;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    line-height: 28px;
  }
`;
const ImageLink = styled(Link)`
  height: 245px;
  min-height: 245px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    max-height: 250px;
  }
`;
const Excerpt = styled(Link)`
  margin-bottom: 10px;
  line-height: 1.5;
  color: var(--color-black);
  text-decoration: none;
  font-size: 18px;
  font-weight: 400;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 14px;
  }
  p {
    font-size: 1rem;
  }
  br {
    line-height: 0;
    display: block;
    margin: 0;
    padding: 0;
  }
`;
const StyledLink = styled(Link)`
  text-decoration: none;
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-size: 18px;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 16px;
  }
  width: fit-content;
  color: inherit;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
      span {
        padding-left: 15px;
      }
    }
  }
  span {
    padding-left: 8px;
    margin-top: 5px;
    transition: all 0.3s ease;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin: 15px 0;
  }
`;

export default function SearchResult({
  imgSrc,
  date,
  title,
  excerpt,
  link,
  imgAlt,
  image,
  columns,
  excerptLength,
  parentCategoryName,
}) {
  const truncate = (str) => {
    if (str) {
      const words = str.split(" ");
      return words.length > excerptLength
        ? words.slice(0, excerptLength).join(" ") + "..."
        : str.replace("[&hellip;]", "...");
    } else {
      return "";
    }
  };

  return (
    <SearchResultsGrid columns={columns}>
      {(image || imgSrc) && (
        <ImageLink to={link}>
          <Image alt={imgAlt} src={imgSrc} image={image} itemProp="image" />
        </ImageLink>
      )}
      <TextDiv>
        {date && <Date>Publisert: {date}</Date>}
        <Title>{`${title} ${
          parentCategoryName ? `(${parentCategoryName})` : ""
        }`}</Title>
        {excerpt && (
          <Excerpt
            to={link}
            dangerouslySetInnerHTML={{
              __html: truncate(excerpt),
            }}
          />
        )}

        {link && (
          <StyledLink to={link} itemProp="url">
            {_("Les mer")}
            <span>
              <FontAwesomeIcon icon={faArrowRightLong} size="1x" />
            </span>
          </StyledLink>
        )}
      </TextDiv>
    </SearchResultsGrid>
  );
}

SearchResult.propTypes = {
  imgSrc: PropTypes.object,
  date: PropTypes.string,
  title: PropTypes.string,
  author: PropTypes.string,
  excerpt: PropTypes.string,
  link: PropTypes.string,
  imgAlt: PropTypes.string,
  image: PropTypes.object,
  columns: PropTypes.number,
  excerptLength: PropTypes.number,
  parentCategoryName: PropTypes.string,
};
SearchResult.defaultProps = {
  imgSrc: undefined,
  date: undefined,
  title: undefined,
  author: undefined,
  excerpt: undefined,
  link: undefined,
  imgAlt: undefined,
  image: undefined,
  columns: undefined,
  excerptLength: 100,
  parentCategoryName: undefined,
};
